import styled from '@emotion/styled'

interface IconProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  iconSize?: number
}

interface ContentProps {
  contentSize: keyof typeof CONTENT_WIDTH
  isVisible: boolean
  placement: string
  targetSize: number
  dark?: boolean
}

const base = {
  padding: {
    double: '20px',
    single: '10px',
    half: '5px',
  },
  colors: {
    default: '#f5f5f5',
    dark: '#1A1A1A',
    shadow: '#cecece',
  },
  caret: {
    borderWidth: '6px',
    position: 'calc(100% - 1px)',
  },
}

const CONTENT_WIDTH = {
  small: '170px',
  normal: '240px',
}

const showContent = `
  opacity: 1;
  visibility: visible;
`

const contentPlacement = ({
  placement,
  targetSize,
  color,
}: {
  placement: string
  targetSize: number
  color: keyof typeof base.colors
}) => {
  const sizes = {
    half: `${targetSize / 2}px`,
  }

  const targetSizeWithCaret = `calc(${sizes.half} - ${base.caret.borderWidth} * 2)`

  switch (placement) {
    case 'top':
      return `
        box-shadow: ${base.colors.shadow} 0px -5px 15px -5px;
        bottom: 100%;
        left: 50%;
        transform: translate(-50%, -5px);

        &::after {
          left: 50%;
          top: ${base.caret.position};
          transform: translateX(-50%);
          border-color: ${base.colors[color]} transparent transparent
            transparent;
        }
      `
    case 'right':
      return `
        box-shadow: ${base.colors.shadow} 0px 10px 10px -10px;
        top: 50%;
        left: 100%;
        transform: translate(5px, -50%);

        &::after {
          top: 50%;
          right: ${base.caret.position};
          transform: translateY(-50%);
          border-color: transparent ${base.colors[color]} transparent
            transparent;
        }
      `
    case 'left':
      return `
        box-shadow: ${base.colors.shadow} 0px 10px 10px -10px;
        top: 50%;
        right: 100%;
        transform: translate(-5px, -50%);

        &::after {
          top: 50%;
          left: ${base.caret.position};
          transform: translateY(-50%);
          border-color: transparent transparent transparent
            ${base.colors[color]};
        }
      `
    case 'bottomLeft':
      return `
        box-shadow: ${base.colors.shadow} 4px 5px 7px -5px;
        top: 100%;
        right: ${targetSizeWithCaret};
        transform: translateY(5px);

        &::after {
          right: ${base.caret.borderWidth};
          bottom: ${base.caret.position};
          border-color: transparent transparent ${base.colors[color]}
            transparent;
        }
      `
    case 'bottomRight':
      return `
        box-shadow: ${base.colors.shadow} -4px 5px 7px -5px;
        top: 100%;
        left: ${targetSizeWithCaret};
        transform: translateY(5px);

        &::after {
          left: ${base.caret.borderWidth};
          bottom: ${base.caret.position};
          border-color: transparent transparent ${base.colors[color]}
            transparent;
        }
      `
    case 'topLeft':
      return `
        box-shadow: ${base.colors.shadow} 4px -5px 7px -5px;
        bottom: 100%;
        right: ${targetSizeWithCaret};
        transform: translateY(-5px);

        &::after {
          right: ${base.caret.borderWidth};
          top: ${base.caret.position};
          border-color: ${base.colors[color]} transparent transparent
            transparent;
        }
      `
    case 'topRight':
      return `
        box-shadow: ${base.colors.shadow} -4px -5px 7px -5px;
        bottom: 100%;
        left: ${targetSizeWithCaret};
        transform: translateY(-5px);

        &::after {
          left: ${base.caret.borderWidth};
          top: ${base.caret.position};
          border-color: ${base.colors[color]} transparent transparent
            transparent;
        }
      `
    default:
      return `
        box-shadow: ${base.colors.shadow} 0px 5px 7px -5px;
        top: 100%;
        left: 50%;
        transform: translate(-50%, 5px);

        &::after {
          left: 50%;
          bottom: ${base.caret.position};
          transform: translateX(-50%);
          border-color: transparent transparent ${base.colors[color]}
            transparent;
        }
      `
  }
}

export const Wrapper = styled.div`
  position: relative;
`

export const Target = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

export const Content = styled.div<ContentProps>(
  ({ contentSize, isVisible, placement, targetSize, dark }) => `
  background-color: ${dark ? base.colors.dark : base.colors.default};
  color: ${dark && base.colors.default};
  border-radius: 10px;
  font-size: 14px;
  padding: ${base.padding.single} ${base.padding.double};
  position: absolute;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease;
  width: max-content;
  min-width: fit-content;
  line-height: 19px;
  z-index: 10;
  max-width: ${CONTENT_WIDTH[contentSize]};

  &::after {
    content: '';
    border-width: ${base.caret.borderWidth};
    position: absolute;
    border-style: solid;
  }

  ${isVisible && showContent}
  ${contentPlacement({
    placement,
    targetSize,
    color: dark ? 'dark' : 'default',
  })}
`
)

export const Icon = styled.img<IconProps>(
  ({ iconSize }) => `
  width: ${iconSize}px;
  height: ${iconSize}px;
`
)
